import { useState } from 'react';
import { formatCents } from '@affily/shared/money';
import { api } from '../lib/api.js';
import { useFetch } from '../hooks/useFetch.js';
import { Button, Badge, Alert, SectionTitle, Spinner, EmptyState, Select, Input } from './ui.jsx';
import { HelpLink } from '../routes/Help.jsx';

/**
 * Pick your stack, get working code.
 *
 * The credentials are already in the snippets, so nobody has to splice a token
 * into an example and get the HMAC input string subtly wrong. Every stack leads
 * with catching `aff_click` on landing, because forgetting that is the single
 * most common reason a brand's conversions arrive unattributed.
 */
/** `bare` drops the heading and the stack buttons, for a picker that already chose one. */
export function StackPicker({ stacks = [], bare = false }) {
  const [active, setActive] = useState(stacks.find((s) => s.recommended)?.id ?? stacks[0]?.id);
  const [copied, setCopied] = useState(null);
  // The same list StoreConnect reads: which stores this server can connect in one click.
  const oneClick = useFetch('/connections').data?.available ?? {};

  const stack = stacks.find((s) => s.id === active);
  if (!stack) return null;

  async function copy(text, key) {
    await navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <section>
      {!bare && (
        <SectionTitle hint="For a site with no store to connect, or one you’d rather not connect. Your credentials are already in the code.">
          Or install tracking with code
        </SectionTitle>
      )}

      <div className={`flex flex-wrap gap-1.5 ${bare ? 'hidden' : ''}`}>
        {stacks.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActive(s.id)}
            aria-pressed={s.id === active}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              s.id === active
                ? 'border-accent bg-accent text-white'
                : 'border-rule-strong bg-paper-raised text-ink-muted hover:text-ink'
            }`}
          >
            {s.label}
            {s.recommended && s.id !== active && (
              <span className="ml-1.5 text-2xs text-positive">best</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-4 rounded-lg border border-rule bg-paper-raised p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="max-w-xl text-sm text-ink-muted">{stack.summary}</p>
          <Badge tone={stack.kind === 'server' ? 'positive' : 'caution'}>
            {stack.kind === 'server' ? 'Server-to-server' : 'Browser only'}
          </Badge>
        </div>

        {stack.id === 'woocommerce' && oneClick.woocommerce && (
          <Alert tone="info" className="mt-3">
            Connecting WooCommerce above does all of this with no code: it reports every paid
            order, reads the creator’s click from WooCommerce’s own order attribution, and makes
            creators’ codes as coupons. Use the code only if you’d rather not connect.
          </Alert>
        )}

        {stack.kind === 'no-server' && (
          <Alert tone="caution" className="mt-3">
            Browser tracking is blockable and forgeable. If you take payments through Stripe,
            connect Stripe instead — it needs no code and can’t be blocked.
          </Alert>
        )}

        <ol className="mt-4 space-y-5">
          {stack.steps.map((step, i) => (
            <li key={step.title}>
              <div className="flex items-baseline gap-2">
                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-ink text-2xs font-semibold text-paper">
                  {i + 1}
                </span>
                <h3 className="text-sm font-medium">{step.title}</h3>
              </div>

              {step.note && <p className="mt-1 pl-7 text-xs text-ink-faint">{step.note}</p>}

              {step.code && (
                <div className="mt-2 pl-7">
                  <pre className="overflow-x-auto rounded-lg border border-rule bg-ink p-3 text-xs leading-relaxed text-paper">
                    <code>{step.code}</code>
                  </pre>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={() => copy(step.code, `${stack.id}-${i}`)}
                  >
                    {copied === `${stack.id}-${i}` ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/**
 * What actually arrived.
 *
 * Installing tracking and then waiting to see whether commissions appear is the
 * classic silent failure. This turns it into something a brand can debug in
 * thirty seconds: here is what we received, and here is exactly why anything
 * didn't earn a commission.
 *
 * It is also where the brand records what a tracked action led to — the
 * viewing that became a sale — for actions only they can witness (`manualOnly`
 * events). That credits the creator who brought the viewing.
 */
export function TrackingActivity({ programId, events = [] }) {
  const [event, setEvent] = useState('');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const params = (extra = {}) => {
    const p = new URLSearchParams({ ...(event && { event }), ...(q && { q }), ...extra }).toString();
    return p ? `?${p}` : '';
  };
  const { data, loading, reload } = useFetch(`/programs/${programId}/activity${params()}`);
  const [older, setOlder] = useState({ rows: [], hasMore: null });
  const [error, setError] = useState('');

  const first = data?.conversions ?? [];
  const rows = [...first, ...older.rows];
  const hasMore = older.hasMore ?? data?.hasMore ?? false;
  const recordable = events.filter((e) => e.manualOnly && e.isActive !== false);
  // Searching and filtering an empty list is noise: they appear once there is
  // something to look through, and stay while a search or filter is on.
  const showTools = rows.length > 0 || Boolean(q) || Boolean(event);

  function refresh() {
    setOlder({ rows: [], hasMore: null });
    setError('');
    reload();
  }

  async function loadMore() {
    const last = rows[rows.length - 1];
    try {
      const more = await api.get(`/programs/${programId}/activity${params({ before: last.id })}`);
      setOlder((o) => ({ rows: [...o.rows, ...more.conversions], hasMore: more.hasMore }));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <section>
      <SectionTitle
        hint="Everything we received for this program, newest first"
        action={
          <div className="flex flex-wrap items-center gap-2">
            {/* A house can sell years after the viewing: find that viewing by its id. */}
            {showTools && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setQ(search.trim());
                  setOlder({ rows: [], hasMore: null });
                }}
              >
                <Input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by order number"
                  className="h-8 w-48 py-0 text-xs"
                  aria-label="Search by order number"
                />
              </form>
            )}
            {showTools && events.length > 1 && (
              <Select
                value={event}
                onChange={(e) => {
                  setEvent(e.target.value);
                  setOlder({ rows: [], hasMore: null });
                }}
                className="h-8 py-0 text-xs"
                aria-label="Show one action"
              >
                <option value="">All actions</option>
                {events.map((e) => (
                  <option key={e.key} value={e.key}>
                    {e.name}
                  </option>
                ))}
              </Select>
            )}
            <Button size="sm" variant="outline" onClick={refresh}>
              Refresh
            </Button>
          </div>
        }
      >
        Is it working?
      </SectionTitle>

      <Alert>{error}</Alert>
      {loading ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <EmptyState title={q ? `No order id contains “${q}”` : event ? 'None of these yet' : 'Nothing received yet'}>
          Once your site reports a conversion it shows up here within seconds — along with why it
          did or didn’t earn anyone a commission.
        </EmptyState>
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-rule bg-paper-raised">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Received</th>
                  <th>Action</th>
                  <th>Order</th>
                  <th className="num">Value</th>
                  <th>Via</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <ActivityRow
                    key={c.id}
                    row={c}
                    programId={programId}
                    recordable={recordable.filter(
                      (e) => e.key !== c.event && !c.followUps?.some((f) => f.event === e.key && !f.undone),
                    )}
                    onRecorded={refresh}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {hasMore && (
            <Button size="sm" variant="ghost" className="mt-2" onClick={loadMore}>
              Load older
            </Button>
          )}
        </>
      )}
    </section>
  );
}

const ORDER_STATE = { awaiting_payment: 'awaiting payment', cancelled: 'cancelled' };

function ActivityRow({ row: c, programId, recordable, onRecorded }) {
  const [recording, setRecording] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  /** Take back a sale recorded by mistake, while it is not yet settled. */
  async function undo(f) {
    if (!window.confirm(`Undo the ${f.event} recorded from this ${c.event}? You can record it again afterwards.`)) return;
    setError('');
    try {
      await api.post(`/programs/${programId}/conversions/${f.id}/undo`);
      onRecorded();
    } catch (err) {
      setError(err.message);
    }
  }

  // What this creator's own terms pay for it; null when they joined before it existed.
  const pays = recording ? c.followUpTerms?.[recording.key] : undefined;

  async function record() {
    // Typed as text so "1 200 000" or "1200000,50" is read rather than silently
    // dropped the way a number field drops what it cannot parse.
    const typed = amount.replace(/\s/g, '').replace(',', '.');
    if (typed !== '' && !/^\d+(\.\d{1,2})?$/.test(typed)) {
      setError('Enter the value as a plain number, like 1200000 or 1200000.50');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await api.post(`/programs/${programId}/conversions/${c.id}/follow-up`, {
        event: recording.key,
        ...(typed !== '' ? { amountCents: Math.round(Number(typed) * 100) } : {}),
      });
      setRecording(null);
      onRecorded();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <td className="whitespace-nowrap text-ink-muted">{new Date(c.receivedAt).toLocaleString()}</td>
        <td>
          <code className="text-xs">{c.event}</code>
          {c.billingCycle > 1 && <span className="ml-1 text-xs text-ink-faint">cycle {c.billingCycle}</span>}
          {c.followUps?.map((f) =>
            f.undone ? (
              <span key={f.id} className="mt-0.5 block text-xs text-ink-faint line-through">
                → {f.event} recorded
              </span>
            ) : (
              <span key={f.id} className="mt-0.5 block text-xs text-positive">
                → {f.event} recorded{' '}
                <button type="button" className="text-ink-faint underline hover:text-negative" onClick={() => undo(f)}>
                  undo
                </button>
              </span>
            ),
          )}
        </td>
        <td className="text-xs text-ink-muted">{c.orderId}</td>
        <td className="num">{c.amountCents ? formatCents(c.amountCents, { currency: c.currency }) : '—'}</td>
        <td>
          <Badge tone={c.source === 'postback' ? 'positive' : 'neutral'}>{c.source}</Badge>
        </td>
        <td>
          {c.orderState && c.orderState !== 'refunded' ? (
            <>
              <Badge tone="neutral">{ORDER_STATE[c.orderState]}</Badge>
              <span className="mt-0.5 block text-xs text-ink-faint">
                {c.orderState === 'awaiting_payment'
                  ? `Counted once it is paid${c.creator ? `, for ${c.creator}` : ''}`
                  : 'Never paid, so nothing is owed'}
              </span>
            </>
          ) : c.orderState === 'refunded' ? (
            <>
              <Badge tone="negative">refunded</Badge>
              <span className="mt-0.5 block text-xs text-ink-faint">
                {c.paid ? 'Refunded after it was settled; the commission stands' : 'The commission was reversed'}
              </span>
            </>
          ) : c.problem ? (
            <>
              <Badge tone="negative">not paid</Badge>
              <span className="mt-0.5 block text-xs text-negative">{c.problem}</span>
            </>
          ) : (
            <>
              <Badge tone={c.trackedOnly ? 'neutral' : 'positive'}>{c.trackedOnly ? 'tracked' : 'paid'}</Badge>
              {c.creator && (
                <span className="mt-0.5 block text-xs text-ink-faint">
                  {c.trackedOnly ? 'from' : 'to'} {c.creator}
                  {c.attribution === 'code' && c.promoCode && ` · code ${c.promoCode}`}
                </span>
              )}
            </>
          )}
          {c.credited &&
            !c.orderState &&
            recordable.map((e) => (
              <button
                key={e.key}
                type="button"
                onClick={() => setRecording(recording?.key === e.key ? null : e)}
                className="mt-1 block text-xs text-accent hover:underline"
              >
                Record {e.name.toLowerCase()}
              </button>
            ))}
        </td>
      </tr>
      {error && !recording && (
        <tr>
          <td colSpan={6}>
            <Alert>{error}</Alert>
          </td>
        </tr>
      )}
      {recording && (
        <tr>
          <td colSpan={6} className="bg-paper-sunken">
            <div className="flex flex-wrap items-end gap-3 py-1">
              <p className="max-w-md text-sm">
                {pays === null ? (
                  <>
                    {c.creator ?? 'This creator'} joined before {recording.name.toLowerCase()} was one of
                    your actions, so their terms do not cover it.
                  </>
                ) : c.followUpNotPaid ? (
                  <>
                    Recorded, not paid: {c.followUpNotPaid.charAt(0).toLowerCase() + c.followUpNotPaid.slice(1)}.
                  </>
                ) : (
                  <>
                    Record a <strong>{recording.name.toLowerCase()}</strong> for this {c.event}. It is
                    credited to {c.creator ?? 'the creator who brought it'}, however long ago the{' '}
                    {c.event} was, and pays what their terms say: <strong>{pays ?? recording.summary}</strong>.{' '}
                    <HelpLink slug="recording-sales-yourself">How recorded sales work</HelpLink>
                  </>
                )}
              </p>
              <label className="text-xs text-ink-muted">
                Value (optional)
                <Input
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="mt-0.5 h-8 w-36"
                />
              </label>
              <Button size="sm" onClick={record} disabled={busy || pays === null}>
                {busy ? 'Recording…' : `Record ${recording.name.toLowerCase()}`}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setRecording(null)}>
                Cancel
              </Button>
            </div>
            <Alert>{error}</Alert>
          </td>
        </tr>
      )}
    </>
  );
}
